import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ListItem,
  Switch,
} from '@/components/ui';

import React from 'react';

import {
  IconColumns2,
  IconEye,
  IconEyeOff,
  IconLock,
} from '@tabler/icons-react';
import { Table } from '@tanstack/react-table';
import { DataTableColumnMeta } from './types';

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>;
}

export function DataTableViewOptions<TData>({
  table,
}: DataTableViewOptionsProps<TData>) {
  const columns = table.getAllColumns().filter(
    column =>
      // Show all columns that have accessors and headers, but exclude system columns
      (typeof column.accessorFn !== 'undefined' || column.id !== 'select') &&
      !(column.columnDef.meta as DataTableColumnMeta)?.systemColumn // Skip system columns (like actions)
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const requiredColumns = columns.filter(
    column =>
      (column.columnDef.meta as DataTableColumnMeta)?.required ||
      !column.getCanHide()
  );
  const optionalColumns = columns.filter(
    column =>
      !(column.columnDef.meta as DataTableColumnMeta)?.required &&
      column.getCanHide()
  );

  const visibleOptionalColumns = optionalColumns.filter(column =>
    column.getIsVisible()
  );
  const hiddenOptionalColumns = optionalColumns.filter(
    column => !column.getIsVisible()
  );
  const hiddenCount = hiddenOptionalColumns.length;

  const toggleAllColumns = (visible: boolean) => {
    // Only toggle optional columns, required columns stay visible
    optionalColumns.forEach(column => {
      column.toggleVisibility(visible);
    });
  };

  const hasVisibleOptionalColumns = visibleOptionalColumns.length > 0;
  const hasHiddenOptionalColumns = hiddenOptionalColumns.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button variant="outlined" size="small" startIcon={<IconColumns2 />}>
          Columns
          {hiddenCount > 0 && ` (${hiddenCount} hidden)`}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="data-table-view-options"
        variant="soft"
      >
        <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Show/Hide All Actions */}
        <DropdownMenuItem asChild>
          <ListItem
            primary="Show all columns"
            startIcon={<IconEye />}
            onClick={() => toggleAllColumns(true)}
            disabled={!hasHiddenOptionalColumns}
          />
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <ListItem
            primary="Hide all columns"
            startIcon={<IconEyeOff />}
            onClick={() => toggleAllColumns(false)}
            disabled={!hasVisibleOptionalColumns}
          />
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Individual Column Toggles */}
        {columns.map(column => {
          const isVisible = column.getIsVisible();
          const isRequired =
            (column.columnDef.meta as DataTableColumnMeta)?.required ||
            !column.getCanHide();
          const columnName =
            typeof column.columnDef.header === 'string'
              ? column.columnDef.header
              : column.id;

          return (
            <DropdownMenuItem
              key={column.id}
              className="data-table-column-toggle-item"
              onSelect={e => e.preventDefault()} // Prevent menu close on click
            >
              <div className="data-table-column-toggle">
                <div className="data-table-column-info">
                  <span className="data-table-column-name">{columnName}</span>
                  {isRequired && (
                    <IconLock
                      size={14}
                      className="data-table-column-lock-icon"
                    />
                  )}
                </div>
                <Switch
                  color="primary"
                  highContrast
                  checked={isVisible}
                  onCheckedChange={checked => {
                    if (!isRequired) {
                      column.toggleVisibility(checked);
                    }
                  }}
                  disabled={isRequired}
                  size="small"
                />
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
